
import { Component, Prop, Vue} from 'vue-property-decorator'
import { GetUploadCredentials } from '@/api/leaks'
import { ICredentials } from '@/api/types'
import { createDirectUploadTask, FileData } from 'qiniu-js'
import { formatDate } from '@/utils'
import Compressor from 'compressorjs'
import { IsMobile } from '@/utils/check'

interface FileItem {
  id: string // 唯一ID
  file: File // 文件对象
  name: string // 文件名
  size: string // 文件大小（格式化后）
  percentage: number // 上传进度
  status: 'ready' | 'uploading' | 'success' | 'error' // 上传状态
  uploadTask?: any // 上传任务
  url?: string // 上传成功后的URL
  key?: string // 文件对象存储路径
}

interface UploadFileInfo {
  file: File
  size: string
}

interface ProgressInfo {
  file: File
  percentage: number
  id: string
}

interface SuccessInfo {
  response: any
  file: File
  url: string
  id: string
}

interface ErrorInfo {
  error: Error
  file: File
  id: string
}

@Component({
  name: 'QiniuUpload'
})
export default class QiniuUpload extends Vue {
  // Props
  @Prop({
    type: String,
    required: true,
    validator: (value: string) => ['image', 'video'].indexOf(value) !== -1
  })
  private fileType!: string

  @Prop({
    type: String,
    default: 'default',
    validator: (value: string) => ['check', 'video', 'reference', 'default'].indexOf(value) !== -1
  })
  private uploadType!: string

  @Prop({
    type: String,
    default: 'choice',
    validator: (value: string) => ['direct', 'choice'].indexOf(value) !== -1
  })
  private captureMode!: string

  @Prop({ type: Boolean, default: true })
  private showFileList!: boolean

  @Prop({ type: Boolean, default: false })
  private multiple!: boolean

  @Prop({ type: String, default: '' })
  private accept!: string

  @Prop({ type: String, default: '' })
  private tip!: string

  @Prop({ type: String, default: '选择文件' })
  private buttonText!: string

  @Prop({
    type: Number,
    default(this: QiniuUpload) {
      return this.fileType === 'video' ? 50 : 10
    }
  })
  private maxSize!: number

  // 图片压缩相关属性
  @Prop({ type: Boolean, default: true })
  private enableCompression!: boolean

  @Prop({ type: Number, default: 0.8 })
  private compressionQuality!: number

  @Prop({ type: Number, default: 800 })
  private maxImageWidth!: number

  @Prop({ type: Number, default: 800 })
  private maxImageHeight!: number

  // 数据
  private uploadProgress = 0
  private uploadStatus: string | undefined = ''
  private showProgress = false
  private fileList: FileItem[] = []
  private uploadingCount = 0
  private qiniuToken = ''
  private qiniuDomain = '' // 存储七牛云域名
  private qiniuRegion = '' // 存储七牛云区域信息
  private uploadPrefix = '' // 上传前缀，完全在组件内部处理
  // 备用的七牛云域名，当服务器未返回endpoint时使用
  // 注意: 这里需要替换为您的实际七牛云域名
  // 可以在七牛云控制台 -> 存储空间 -> 空间设置 -> 域名设置中找到
  private readonly fallbackDomain = process.env.VUE_APP_QINIU_DOMAIN || 'your-production-qiniu-domain.com'

  // 生命周期钩子
  // created(): void {
  //   // 初始化内部前缀
  //   this.internalUploadPrefix = this.uploadPrefix
  // }

  // 监听属性变化
  // @Watch('uploadPrefix')
  // onUploadPrefixChange(newValue: string): void {
  //   this.internalUploadPrefix = newValue
  // }

  // 计算属性
  get maxSizeText(): string {
    return this.maxSize >= 1024 ? `${(this.maxSize / 1024).toFixed(1)}GB` : `${this.maxSize}MB`
  }

  // 选择文件
  selectFile(): void {
    // 检测是否是移动设备
    const isMobile = IsMobile()

    // 如果是移动设备并且使用choice模式，尝试使用现代文件选择器API
    if (isMobile && this.captureMode === 'choice' && 'showPicker' in HTMLInputElement.prototype) {
      try {
        // 确保类型断言正确
        const fileInput = this.$refs.fileInput as HTMLInputElement & { showPicker?: () => void }
        if (fileInput && fileInput.showPicker) {
          fileInput.showPicker()
          return
        }
      } catch (error) {
        console.error('使用现代文件选择API失败:', error)
        // 如果新API失败，回退到传统方法
      }
    }

    // 传统方法，直接点击文件输入框
    if (this.$refs.fileInput) {
      (this.$refs.fileInput as HTMLInputElement).click()
    }
  }

  created() {
    this.getQiniuToken()
  }

  // 处理文件选择
  async handleFileChange(e: Event): Promise<void> {
    const files = (e.target as HTMLInputElement).files
    if (!files || files.length === 0) return

    // 转换为数组
    const fileArray = Array.from(files)

    // 处理所有选择的文件
    for (const file of fileArray) {
      const fileSizeMB = file.size / 1024 / 1024
      if (fileSizeMB > this.maxSize) {
        this.$message.error(`文件【${file.name}】大小超过限制（${this.maxSizeText}）`)
        continue
      }

      // 为每个文件创建唯一ID
      const fileId = Date.now() + Math.random().toString(36).substr(2, 9)

      // 添加到文件列表，先显示为准备状态
      const fileItem: FileItem = {
        id: fileId,
        file: file, // 暂时使用原始文件
        name: file.name,
        size: this.formatFileSize(file.size),
        percentage: 0,
        status: 'ready'
      }

      this.fileList.push(fileItem)

      // 发出上传前事件
      this.$emit('before-upload', {
        file,
        size: fileSizeMB.toFixed(2)
      } as UploadFileInfo)

      try {
        // 根据文件类型处理文件
        if (this.enableCompression && this.fileType === 'image' && file.type.startsWith('image/')) {
          // 对图片文件进行压缩
          await this.compressAndUploadImage(file, fileItem)
        } else {
          // 非图片文件或不需要压缩的图片直接上传
          const newFileName = this.getKey(file.name)
          const newFile = new File([file], newFileName, {
            lastModified: file.lastModified,
            type: file.type
          })
          fileItem.file = newFile
          await this.uploadFile(fileItem)
        }
      } catch (error) {
        console.error('处理文件失败:', error)
        fileItem.status = 'error'
        this.$message.error(`文件【${file.name}】处理失败: ${(error as Error).message || '未知错误'}`)
      }
    }

    // 清空文件选择器，以便下次选择同一文件时能触发change事件
    (e.target as HTMLInputElement).value = ''
  }

  getKey(file_name :string): string {
      // 设置上传文件key
      // 始终使用服务器返回的前缀加上额外的类型路径和文件名
      const timestamp = Date.now(); // 当前时间戳

      let prefix = this.uploadPrefix || ''

      // 确保前缀以/结尾
      if (prefix && !prefix.endsWith('/')) {
        prefix += '/'
      }

      // 根据上传类型添加额外的路径
      let typePath = this.uploadType + '/' + formatDate(timestamp, 'YYYY/MM/DD') + '/'

      // 组合最终的key
      const key = prefix + typePath + timestamp + '_' + file_name;
      return key
  }

  // 上传文件，使用新的七牛SDK
  async uploadFile(fileItem: FileItem): Promise<void> {
    try {
      if (!this.qiniuToken) {
        // 获取上传凭证
        await this.getQiniuToken()
      }

      // 设置文件状态
      fileItem.status = 'uploading'
      this.uploadingCount++
      this.updateTotalProgress()

      fileItem.key = fileItem.file.name
      console.log('上传文件使用的完整key:', fileItem.key)

      // 创建文件数据
      const fileData: FileData = {
        type: 'file',
        data: fileItem.file
      }

      // 创建上传配置
      const uploadConfig = {
        // 使用函数返回token，尽管这里直接返回已获取的token
        tokenProvider: () => Promise.resolve(this.qiniuToken),
        key: fileItem.key,
      }

      console.log('上传配置:', {
        key: uploadConfig.key,
        domain: this.qiniuDomain,
        region: this.qiniuRegion
      })

      // 创建直传任务
      const uploadTask = createDirectUploadTask(fileData, uploadConfig)

      // 保存上传任务引用以便后续取消
      fileItem.uploadTask = uploadTask

      // 设置进度回调
      uploadTask.onProgress((progress) => {
        // 更新进度
        fileItem.percentage = progress.percent * 100
        this.updateTotalProgress()

        // 触发进度回调
        this.$emit('on-progress', {
          file: fileItem.file,
          percentage: fileItem.percentage,
          id: fileItem.id
        } as ProgressInfo)
      })

      // 设置完成回调
      uploadTask.onComplete((result) => {
        // 上传完成
        fileItem.status = 'success'
        this.uploadingCount--
        this.updateTotalProgress()

        // 解析结果，从JSON字符串转为对象
        const res = typeof result === 'string' ? JSON.parse(result) : result
        console.log('原始上传key:', fileItem.key)
        console.log('七牛返回key:', res.key)

        // 从响应中构建文件URL（使用原始完整key而不是响应中的key）
        const fileUrl = this.buildFileUrl(fileItem.key || '', res)
        fileItem.url = fileUrl

        // 触发成功回调
        this.$emit('on-success', {
          response: res,
          file: fileItem.file,
          url: fileUrl,
          id: fileItem.id
        } as SuccessInfo)

        this.$message.success(`文件【${fileItem.name}】上传成功`)
      })

      // 设置错误回调
      uploadTask.onError((error) => {
        console.error('七牛上传错误:', error)
        fileItem.status = 'error'
        this.uploadingCount--
        this.updateTotalProgress()

        // 错误对象可能为undefined，创建一个标准错误
        const errorObj = error || new Error('未知上传错误')

        // 触发错误回调
        this.$emit('on-error', {
          error: errorObj,
          file: fileItem.file,
          id: fileItem.id
        } as ErrorInfo)

        this.$message.error(`文件【${fileItem.name}】上传失败: ${(error && error.message) || '未知错误'}`)
      })

      // 启动上传任务
      uploadTask.start()

      // 通知上传开始
      this.$emit('on-start', fileItem.file)
    } catch (error) {
      console.error('准备上传失败:', error)
      fileItem.status = 'error'
      this.$message.error(`文件【${fileItem.name}】准备上传失败: ${(error as Error).message || '未知错误'}`)
    }
  }

  // 构建文件URL
  buildFileUrl(key: string, res: any): string {
    // 从响应中获取URL信息
    console.log('构建URL的原始响应:', res)
    console.log('使用的key:', key)

    // 这里有两种情况：
    // 1. 如果七牛云返回了完整路径，直接使用
    // 2. 如果只返回了key，则使用domain + key组成URL

    // 尝试直接从res中获取完整URL
    if (res && res.url) {
      console.log('使用返回的URL:', res.url)
      return res.url
    }

    // 有时七牛返回完整的downloadUrl
    if (res && res.downloadUrl) {
      console.log('使用返回的downloadUrl:', res.downloadUrl)
      return res.downloadUrl
    }

    // 使用域名（服务器提供的或备用的）和完整key组成URL
    let domain = this.qiniuDomain

    // 检查七牛云返回的域名
    if (!domain || domain === 'your-actual-qiniu-domain.com' || domain === 'your-production-qiniu-domain.com') {
      // 日志记录
      console.warn('七牛云域名无效或未设置:', domain)

      // 尝试使用备用域名
      if (this.fallbackDomain &&
          this.fallbackDomain !== 'your-actual-qiniu-domain.com' &&
          this.fallbackDomain !== 'your-production-qiniu-domain.com') {
        domain = this.fallbackDomain
        console.log('使用备用域名:', domain)
      } else {
        // 如果备用域名也无效，尝试使用硬编码的生产环境域名
        console.error('备用域名也无效，尝试使用默认生产环境域名')

        // 替换为您的实际七牛云域名
        const productionDomain = '您的七牛云实际域名.com'

        if (productionDomain !== '您的七牛云实际域名.com') {
          domain = productionDomain
          console.log('使用硬编码的生产环境域名:', domain)
        } else {
          console.error('请在代码中设置正确的七牛云域名！')
          // 返回原始key作为URL，这可能不会正常工作，但至少不会报错
          return key
        }
      }
    }

    // 确保域名是以http开头的完整URL
    if (!domain.startsWith('http')) {
      // 优先使用HTTPS协议，特别是在生产环境
      domain = `https://${domain}`
    } else if (domain.startsWith('http://') && window.location.protocol === 'https:') {
      // 如果当前页面是HTTPS但域名是HTTP，将其升级为HTTPS
      domain = `https://${domain.substring(7)}`
    }

    // 确保域名末尾有/
    const formattedDomain = domain.endsWith('/') ? domain : `${domain}/`

    // 确保key不以/开头
    const formattedKey = key.startsWith('/') ? key.substring(1) : key

    const finalUrl = `${formattedDomain}${formattedKey}`
    console.log('构建的完整URL:', finalUrl)
    return finalUrl
  }

  // 获取七牛上传凭证
  async getQiniuToken(): Promise<void> {
    try {
      const { data } = await GetUploadCredentials()

      console.log('获取到的凭证数据:', JSON.stringify(data))

      if (!data) {
        throw new Error('获取上传凭证失败')
      }

      const credentials = data as ICredentials
      this.qiniuToken = credentials.sessionToken

      // 存储七牛云域名，用于构建URL
      if (credentials.endpoint) {
        this.qiniuDomain = credentials.endpoint
        console.log('服务器返回的七牛域名(endpoint):', this.qiniuDomain)
      } else {
        // 如果服务器未返回域名，将使用fallbackDomain
        console.warn('服务器未返回七牛域名(endpoint)，将使用备用域名:', this.fallbackDomain)
        this.qiniuDomain = '' // 保持为空，在buildFileUrl中会使用备用域名
      }

      // 如果返回了region，也保存下来
      if (credentials.region) {
        this.qiniuRegion = credentials.region
        console.log('服务器返回的七牛区域(region):', this.qiniuRegion)
      }

      // 保存服务器返回的路径作为基础前缀
      if (credentials.paths && credentials.paths.length > 0) {
        this.uploadPrefix = credentials.paths[0]
        console.log('服务器返回的上传路径前缀:', this.uploadPrefix)
      } else {
        this.uploadPrefix = ''
        console.warn('服务器未返回上传路径前缀')
      }

    } catch (error) {
      console.error('获取七牛Token失败:', error)
      this.$message.error('获取上传凭证失败，请重试')
      throw error
    }
  }

  // 更新总进度
  updateTotalProgress(): void {
    if (this.fileList.length === 0) {
      this.uploadProgress = 0
      this.showProgress = false
      return
    }

    // 只有上传中才显示进度条
    this.showProgress = this.uploadingCount > 0

    if (!this.showProgress) {
      return
    }

    // 计算总进度
    let totalPercentage = 0
    let completedFiles = 0

    for (const item of this.fileList) {
      if (item.status === 'success') {
        totalPercentage += 100
        completedFiles++
      } else if (item.status === 'uploading') {
        totalPercentage += item.percentage || 0
      }
    }

    // 计算平均进度
    const totalFiles = this.fileList.length
    this.uploadProgress = Math.round(totalPercentage / totalFiles)

    // 设置状态
    if (completedFiles === totalFiles) {
      this.uploadStatus = 'success'
    } else if (this.fileList.some(item => item.status === 'error')) {
      this.uploadStatus = 'exception'
    } else {
      this.uploadStatus = undefined
    }
  }

  // 取消上传
  cancelUpload(fileItem: FileItem): void {
    if (fileItem.uploadTask) {
      fileItem.uploadTask.cancel()
        .then(() => {
          fileItem.status = 'error'
          fileItem.percentage = 0
          this.uploadingCount--
          this.updateTotalProgress()
          this.$message.info(`已取消上传文件【${fileItem.name}】`)
        })
        .catch((err: Error) => {
          console.error('取消上传失败:', err)
          this.$message.error(`取消上传失败: ${err.message}`)
        })
    }
  }

  // 重试上传
  async retryUpload(fileItem: FileItem): Promise<void> {
    // 重置状态
    fileItem.percentage = 0
    fileItem.status = 'ready'

    // 重新上传
    await this.uploadFile(fileItem)
  }

  // 复制URL
  copyUrl(fileItem: FileItem): void {
    if (!fileItem.url) {
      this.$message.error('文件URL不存在')
      return
    }

    // 创建临时文本区域
    const textArea = document.createElement('textarea')
    textArea.value = fileItem.url
    document.body.appendChild(textArea)
    textArea.select()

    // 复制到剪贴板
    try {
      document.execCommand('copy')
      this.$message.success('URL已复制到剪贴板')
    } catch (err) {
      this.$message.error('复制失败，请手动复制')
    }

    // 移除临时元素
    document.body.removeChild(textArea)
  }

  // 格式化文件大小
  formatFileSize(size: number): string {
    if (size < 1024) {
      return size + ' B'
    } else if (size < 1024 * 1024) {
      return (size / 1024).toFixed(2) + ' KB'
    } else if (size < 1024 * 1024 * 1024) {
      return (size / (1024 * 1024)).toFixed(2) + ' MB'
    } else {
      return (size / (1024 * 1024 * 1024)).toFixed(2) + ' GB'
    }
  }

  // 清空文件列表
  clearFiles(): void {
    // 取消所有上传中的文件
    for (const item of this.fileList) {
      if (item.status === 'uploading' && item.uploadTask) {
        item.uploadTask.cancel().catch((err: Error) => {
          console.error('取消上传失败:', err)
        })
      }
    }

    // 清空列表
    this.fileList = []
    this.uploadingCount = 0
    this.updateTotalProgress()
  }

  // 压缩并上传图片
  async compressAndUploadImage(file: File, fileItem: FileItem): Promise<void> {
    return new Promise((resolve, reject) => {
      // 显示压缩中的状态提示
      // this.$message.info(`正在压缩图片【${file.name}】...`)

      new Compressor(file, {
        quality: this.compressionQuality,
        maxWidth: this.maxImageWidth,
        maxHeight: this.maxImageHeight,
        success: async(compressedFile) => {
          try {
            // 展示压缩效果
            const originalSize = file.size / 1024
            const compressedSize = compressedFile.size / 1024
            const compressionRatio = (1 - compressedSize / originalSize) * 100

            console.log(`图片压缩: ${file.name}`)
            console.log(`原始大小: ${originalSize.toFixed(2)} KB`)
            console.log(`压缩后大小: ${compressedSize.toFixed(2)} KB`)
            console.log(`压缩比: ${compressionRatio.toFixed(2)}%`)

            // 如果压缩后的文件比原始文件大，则使用原始文件
            if (compressedFile.size > file.size) {
              console.log('压缩后文件更大，使用原始文件')
              const newFileName = this.getKey(file.name)
              const newFile = new File([file], newFileName, {
                lastModified: file.lastModified,
                type: file.type
              })
              fileItem.file = newFile
            } else {
              // 使用压缩后的文件，但保持原始文件扩展名
              const extension = file.name.split('.').pop() || ''
              const newFileName = this.getKey(`${file.name.split('.')[0]}.${extension}`)

              // 创建新的文件对象用于上传
              const newFile = new File([compressedFile], newFileName, {
                lastModified: file.lastModified,
                type: compressedFile.type
              })

              // 更新文件项的文件对象和大小信息
              fileItem.file = newFile
              fileItem.size = this.formatFileSize(newFile.size)

              // 显示压缩结果
              // this.$message.success(`图片【${file.name}】压缩成功，减小了 ${compressionRatio.toFixed(2)}%`)
            }

            // 开始上传
            await this.uploadFile(fileItem)
            resolve()
          } catch (error) {
            reject(error)
          }
        },
        error: (err) => {
          console.error('图片压缩失败:', err)
          this.$message.error(`图片【${file.name}】压缩失败，将使用原图上传`)

          // 压缩失败，使用原始文件上传
          const newFileName = this.getKey(file.name)
          const newFile = new File([file], newFileName, {
            lastModified: file.lastModified,
            type: file.type
          })
          fileItem.file = newFile

          // 继续上传流程
          this.uploadFile(fileItem).then(resolve).catch(reject)
        }
      })
    })
  }
}
